import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EndpointDialog } from "@/components/models/EndpointDialog";
import { ModelMeta, VendorMark } from "@/components/models/ModelParts";
import { endpointKind } from "@/components/models/vendor-logos";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, type ModelRow, type OrgEndpointView, type OrgModels } from "@/lib/api";
import { relativeIso } from "@/lib/format";
import { describeModelError } from "@/lib/model-errors";
import {
  groupModelRows,
  joinNames,
  jobsSentence,
  modelName,
  pendingFor,
  ROLE_LABELS,
  sourceLabel,
  vendorName,
  vendorOf,
} from "@/lib/models";
import { cn } from "@/lib/utils";
import { Notice } from "@/pages/container-tabs/connections/ConnectionParts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, KeyRound, Lock, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useInvalidateModels, useOrgModels } from "./use-org-models";

// Manage models: every model the organization's Boos could use, built-in
// ones by provider and your own by service, each with an on/off switch.
// Turned-off models leave every list. A model that is a default can't be
// turned off here; its row links to the job's dropdown instead. Takes over
// the screen; Done goes back to the dropdowns.

const MAX_ENDPOINTS = 10;

export function ManageModelsDialog({
  open,
  onOpenChange,
  onChooseFor,
  editEndpoint,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // "Change" on a default: open that job's dropdown.
  onChooseFor: (job: string) => void;
  // Opens straight into editing this service (to enter its key again).
  editEndpoint?: string | null;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="top-0 left-0 flex h-dvh w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none p-0 ring-0 sm:max-w-none data-open:zoom-in-100 data-open:slide-in-from-bottom-2 data-closed:zoom-out-100"
      >
        {open && <ManageModelsBody onDone={() => onOpenChange(false)} onChooseFor={onChooseFor} editEndpoint={editEndpoint} />}
      </DialogContent>
    </Dialog>
  );
}

function ManageModelsBody({
  onDone,
  onChooseFor,
  editEndpoint,
}: {
  onDone: () => void;
  onChooseFor: (job: string) => void;
  editEndpoint?: string | null;
}) {
  const query = useOrgModels();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<OrgEndpointView | "new" | null>(null);
  const invalidate = useInvalidateModels();
  const data = query.data;
  const [openedEdit, setOpenedEdit] = useState(false);
  if (!openedEdit && editEndpoint && data) {
    setOpenedEdit(true);
    setEditing(data.endpoints.find((e) => e.name === editEndpoint) ?? null);
  }
  const canManage = !!data?._permissions.canManage;
  const atLimit = (data?.endpoints.length ?? 0) >= MAX_ENDPOINTS;

  return (
    <>
      <header className="border-b bg-background">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-6 gap-y-3 px-6 py-4 sm:px-8">
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-lg font-semibold">Manage models</DialogTitle>
            <DialogDescription className="mt-1">
              Choose which models your Boos can use. Models you turn off disappear from every list. Changes save as you
              go.
            </DialogDescription>
          </div>
          <div className="flex items-center gap-2">
            {canManage && (
              <Button
                onClick={() => setEditing("new")}
                disabled={atLimit}
                title={atLimit ? `You can connect up to ${MAX_ENDPOINTS} of your own model services.` : undefined}
              >
                <Plus className="size-4" />
                Add your own model
              </Button>
            )}
            <Button variant="outline" onClick={onDone}>
              Done
            </Button>
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl space-y-6 px-6 py-6 sm:px-8">
          {query.isLoading ? (
            <LoadingIndicator />
          ) : query.isError || !data ? (
            <ErrorComponent error={query.error ?? "Failed to load models"} />
          ) : (
            <ModelList
              orgId={query.orgId}
              data={data}
              search={search}
              onSearch={setSearch}
              onEdit={(ep) => setEditing(ep)}
              onChooseFor={onChooseFor}
            />
          )}
        </div>
      </div>

      {data && (
        <EndpointDialog
          open={editing !== null}
          onOpenChange={(o) => !o && setEditing(null)}
          existing={editing === "new" ? null : editing}
          takenNames={data.endpoints.map((e) => e.name)}
          reservedNames={data.reservedEndpointNames}
          purposeDefs={data.purposeDefs}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
        />
      )}
    </>
  );
}

function ModelList({
  orgId,
  data,
  search,
  onSearch,
  onEdit,
  onChooseFor,
}: {
  orgId: string | null | undefined;
  data: OrgModels;
  search: string;
  onSearch: (s: string) => void;
  onEdit: (ep: OrgEndpointView) => void;
  onChooseFor: (job: string) => void;
}) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateModels();
  const canManage = data._permissions.canManage;
  // A refusal stays next to the switch that caused it, until the next change.
  const [refusal, setRefusal] = useState<{ slug: string; message: string } | null>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [removing, setRemoving] = useState<OrgEndpointView | null>(null);
  const [filter, setFilter] = useState<"all" | "on" | "off">("all");
  // Groups start folded; the summary above is the overview.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const isCustom = (slug: string) => data.endpoints.some((e) => slug.startsWith(`${e.name}/`));

  // Jobs each model is the default for: the org's default, else the
  // built-in one where the org chose none.
  const defaultFor = useMemo(() => {
    const out = new Map<string, string[]>();
    for (const def of data.purposeDefs) {
      const slug = data.defaults[def.id] || def.defaultModel;
      if (slug) out.set(slug, [...(out.get(slug) ?? []), def.id]);
    }
    return out;
  }, [data.defaults, data.purposeDefs]);

  const setEnabled = (slug: string, on: boolean) => {
    const key = ["org-models", orgId];
    setPending((p) => new Set(p).add(slug));
    setRefusal(null);
    // Show the switch moving at once; the server's answer settles it.
    queryClient.setQueryData<OrgModels>(key, (old) =>
      old ? { ...old, models: old.models.map((m) => (m.name === slug ? { ...m, enabled: on } : m)) } : old,
    );
    api.models
      .setAvailability({ [slug]: on })
      .then(() => invalidate())
      .catch((err: unknown) => {
        queryClient.setQueryData<OrgModels>(key, (old) =>
          old ? { ...old, models: old.models.map((m) => (m.name === slug ? { ...m, enabled: !on } : m)) } : old,
        );
        setRefusal({ slug, message: describeModelError(err, { defs: data.purposeDefs, slug, isCustom }) });
      })
      .finally(() =>
        setPending((p) => {
          const next = new Set(p);
          next.delete(slug);
          return next;
        }),
      );
  };

  const remove = useMutation({
    mutationFn: (name: string) => api.models.deleteEndpoint(name),
    onSuccess: () => {
      toast.success("Removed. Boos that used its models go back to the organization default.");
      setRemoving(null);
      void invalidate();
    },
    onError: (err) => {
      toast.error(describeModelError(err, { defs: data.purposeDefs, source: removing ? sourceLabel(removing) : undefined }));
      setRemoving(null);
    },
  });

  const q = search.trim().toLowerCase();
  const matches = (m: ModelRow) =>
    (filter === "all" || (filter === "off") === !m.enabled) &&
    (!q ||
      [
        m.name,
        modelName(m.name, { custom: m.source === "custom" }),
        m.endpoint ? sourceLabel(data.endpoints.find((e) => e.name === m.endpoint) ?? { name: m.endpoint }) : vendorName(vendorOf(m.name)),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q));
  const narrowed = !!q || filter !== "all";
  // Groups from every model, so their counts don't change with the filter.
  const allGroups = groupModelRows(data.models, data.endpoints.map((e) => e.name));
  const groups = allGroups
    .map((g) => ({ ...g, all: g.models, models: g.models.filter(matches) }))
    .filter((g) => !narrowed || g.models.length > 0);
  const offCount = data.models.filter((m) => !m.enabled).length;
  const onCount = data.models.length - offCount;
  const providerCount = allGroups.filter((g) => g.kind === "hosted").length;
  const lagging = pendingFor(data.pendingBoos, "disabled_models");
  const isOpen = (key: string) => narrowed || open.has(key);
  const toggleGroup = (key: string, next: boolean) =>
    setOpen((o) => {
      const s2 = new Set(o);
      if (next) s2.add(key);
      else s2.delete(key);
      return s2;
    });

  // Removing a service a default uses would leave that job without a model:
  // say so before asking.
  const removingLabel = removing ? sourceLabel(removing) : "";
  const blockingJobs = removing
    ? data.purposeDefs.filter((d) => data.defaults[d.id]?.startsWith(`${removing.name}/`)).map((d) => d.id)
    : [];

  return (
    <>
      {lagging.length > 0 && offCount > 0 && (
        <Notice tone="info">
          <p className="font-medium">Waiting for an update: {joinNames(lagging, 4)}.</p>
          <p className="text-muted-foreground">
            Until {lagging.length === 1 ? "it updates" : "they update"} (automatically, within a few days){" "}
            {lagging.length === 1 ? "it" : "they"} may still use models you turned off.
          </p>
        </Notice>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-2xl font-semibold tracking-tight tabular-nums">
            {onCount} {onCount === 1 ? "model" : "models"} on
            <span className="text-muted-foreground">, {offCount} off</span>
          </p>
          <p className="text-sm text-muted-foreground">
            From {providerCount} {providerCount === 1 ? "provider" : "providers"}
            {data.endpoints.length > 0
              ? ` and ${data.endpoints.length} of your own ${data.endpoints.length === 1 ? "service" : "services"}`
              : ""}
            . New models from providers arrive turned on.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Show">
            {(
              [
                ["all", "All"],
                ["on", `On (${onCount})`],
                ["off", `Off (${offCount})`],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={filter === value}
                onClick={() => setFilter(value)}
                className={cn(
                  "rounded px-2.5 py-1 text-xs font-medium tabular-nums transition-colors",
                  filter === value ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="relative w-56">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search models"
              aria-label="Search models"
              className="h-8 pl-8"
            />
          </div>
        </div>
      </div>

      {groups.length === 0 && (
        <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
          {narrowed ? "No models match." : "No models to show yet."}
        </p>
      )}

      <div className="space-y-2">
        {groups.map((g) => {
          const endpoint = g.kind === "custom" ? data.endpoints.find((e) => e.name === g.name) : undefined;
          const on = g.all.filter((m) => m.enabled).length;
          return (
            <Collapsible
              key={g.key}
              open={isOpen(g.key)}
              onOpenChange={(next) => toggleGroup(g.key, next)}
              className="overflow-hidden rounded-lg border"
            >
              <CollapsibleTrigger className="group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40">
                {g.kind === "custom" ? (
                  <VendorMark vendor="" custom endpoint={endpointKind(endpoint?.baseUrl, g.name)} size="lg" />
                ) : (
                  <VendorMark vendor={g.name} size="lg" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold">
                      {g.kind === "custom" ? sourceLabel(endpoint ?? { name: g.name }) : vendorName(g.name)}
                    </span>
                    {g.kind === "custom" && (
                      <span className="rounded bg-muted px-1.5 text-[11px] font-medium text-muted-foreground">
                        Your own models
                      </span>
                    )}
                    {endpoint?.keyNeeded && <KeyNeeded />}
                  </span>
                  <span className="block text-xs text-muted-foreground tabular-nums">
                    {on === g.all.length ? `All ${g.all.length} on` : `${on} of ${g.all.length} on`}
                  </span>
                </span>
                <ChevronDown className="size-4 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90" />
              </CollapsibleTrigger>
              <CollapsibleContent className="border-t">
                {endpoint && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b bg-muted/20 px-4 py-2 text-xs text-muted-foreground">
                    <span>{endpoint.hasKey ? "Access key saved" : "No access key"}</span>
                    {endpoint.lastProbe && <span>Connection checked {relativeIso(endpoint.lastProbe.completedAt)}</span>}
                    {canManage && (
                      <span className="ml-auto flex items-center gap-1">
                        <Button size="xs" variant="ghost" onClick={() => onEdit(endpoint)}>
                          <Pencil className="size-3" />
                          Edit
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            remove.reset();
                            setRemoving(endpoint);
                          }}
                        >
                          <Trash2 className="size-3" />
                          Remove
                        </Button>
                      </span>
                    )}
                  </div>
                )}
                {g.models.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-muted-foreground">No models yet. Edit it to add one.</p>
                ) : (
                  <ul className="divide-y">
                    {g.models.map((m) => (
                      <ModelSwitchRow
                        key={m.name}
                        row={m}
                        defaultJobs={defaultFor.get(m.name)}
                        defs={data.purposeDefs}
                        refusal={refusal?.slug === m.name ? refusal.message : undefined}
                        busy={pending.has(m.name)}
                        canManage={canManage}
                        onChange={(v) => setEnabled(m.name, v)}
                        onChooseFor={onChooseFor}
                      />
                    ))}
                  </ul>
                )}
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>

      {blockingJobs.length > 0 ? (
        <ConfirmDialog
          open={removing !== null}
          onOpenChange={(o) => !o && setRemoving(null)}
          title={`${removingLabel} is in use`}
          description={`${removingLabel} is the default for ${jobsSentence(blockingJobs, data.purposeDefs)}. Choose another model for ${blockingJobs.length === 1 ? "that job" : "those jobs"} before removing ${removingLabel}.`}
          confirmLabel="Choose another model"
          variant="default"
          onConfirm={() => {
            setRemoving(null);
            onChooseFor(blockingJobs[0]);
          }}
        />
      ) : (
        <ConfirmDialog
          open={removing !== null}
          onOpenChange={(o) => !o && setRemoving(null)}
          title={`Remove ${removingLabel}?`}
          description={`Boos that picked ${joinNames(
            (removing?.models ?? []).map((m) => m.id),
            3,
          ).replace(/ and /, " or ")} switch back to the organization default, and the saved access key is deleted.`}
          confirmLabel="Remove"
          variant="destructive"
          loading={remove.isPending}
          onConfirm={() => removing && remove.mutate(removing.name)}
        />
      )}
    </>
  );
}

function ModelSwitchRow({
  row,
  defaultJobs,
  defs,
  refusal,
  busy,
  canManage,
  onChange,
  onChooseFor,
}: {
  row: ModelRow;
  defaultJobs?: string[];
  defs: OrgModels["purposeDefs"];
  refusal?: string;
  busy: boolean;
  canManage: boolean;
  onChange: (on: boolean) => void;
  onChooseFor: (job: string) => void;
}) {
  const custom = row.source === "custom";
  const id = `model-${row.name.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  // A default stays on; changing the job's model is how it stops being one.
  const locked = !!defaultJobs?.length && row.enabled;
  const jobs = defaultJobs?.length ? jobsSentence(defaultJobs, defs) : "";
  return (
    <li className={cn("flex items-center gap-4 px-4 py-3 transition-colors", !row.enabled && "bg-muted/20")}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <label htmlFor={id} className={cn("text-sm font-medium", !row.enabled && "text-muted-foreground")}>
            {modelName(row.name, { custom })}
          </label>
          {row.role !== "chat" && (
            <span className="rounded bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">{ROLE_LABELS[row.role]}</span>
          )}
        </div>
        {row.summary && <p className="line-clamp-1 text-xs text-muted-foreground">{row.summary}</p>}
        {refusal && (
          <p role="alert" className="mt-0.5 text-xs text-destructive">
            {refusal}
          </p>
        )}
      </div>
      <ModelMeta capabilities={row.capabilities} costTier={row.cost_tier} className="hidden sm:flex" />
      <div className="flex shrink-0 items-center gap-3">
        {locked && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="size-3" />
            <span className="max-w-[16rem] truncate" title={`Default for ${jobs}`}>
              Default for {jobs}
            </span>
            {canManage && defaultJobs && (
              <>
                <span aria-hidden="true">·</span>
                <button
                  type="button"
                  className="font-medium text-foreground underline-offset-2 hover:underline"
                  onClick={() => onChooseFor(defaultJobs[0])}
                >
                  Change
                </button>
              </>
            )}
          </span>
        )}
        <span className={cn("w-6 text-right text-xs", row.enabled ? "text-foreground" : "text-muted-foreground")}>
          {row.enabled ? "On" : "Off"}
        </span>
        {/* A default stays on: the switch is locked until the job's model changes. */}
        <Switch
          id={id}
          checked={row.enabled}
          disabled={!canManage || busy || locked}
          onCheckedChange={onChange}
          aria-label={`${modelName(row.name, { custom })} ${row.enabled ? "on" : "off"}${locked ? `, default for ${jobs}` : ""}`}
        />
      </div>
    </li>
  );
}

function KeyNeeded() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 text-[11px] font-medium text-rose-700 ring-1 ring-rose-200 ring-inset dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30">
      <KeyRound className="size-3" />
      Access key needed
    </span>
  );
}
